import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useState } from 'react';

import { mergeSx } from '../../../theme';

import { LegalDisclaimerPanel } from './LegalDisclaimerPanel';
import { legalDisclaimerStyles as styles } from './LegalDisclaimer.styles';

/**
 * Compact legal disclaimer bar shown at the bottom of every page — the
 * first paragraph of `legalDisclaimerText.ts` plus a "Read more" trigger
 * that opens the full text in `LegalDisclaimerPanel`. Kept in sync by hand
 * with `LEGAL_DISCLAIMER_PARAGRAPHS[0]` since the inline Privacy
 * Policy/Terms links need real JSX, not a plain string.
 *
 * On mobile the same paragraph wraps to far more lines at the narrower
 * width, so it's clamped to 2 lines behind a "Show more" toggle there —
 * desktop always shows it in full.
 */
export function LegalDisclaimer() {
  const [panelOpen, setPanelOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const clamped = isMobile && !expanded;

  return (
    <Box component="footer" sx={styles.root}>
      <Typography sx={mergeSx(styles.text, clamped ? styles.textClamped : undefined)}>
        Worker Solutions LLC (&ldquo;Worker Solutions&rdquo;) aims to keep the
        information on this website accurate and up to date. This information
        may be different than what you see when you visit the government, a
        financial institution, service provider or other website. All
        services and information provided by Worker Solutions or presented on
        this website are provided and presented without warranty. When
        evaluating results from the Opportunity Map, please review
        Worker Solutions&rsquo; <Link href="/privacy-policy" sx={styles.link}>Privacy Policy</Link>, as well as its{' '}
        <Link href="/terms-and-conditions" sx={styles.link}>Terms and Conditions</Link>.{' '}
        <Link
          component="button"
          type="button"
          onClick={() => setPanelOpen(true)}
          sx={styles.link}
        >
          Read more
        </Link>
      </Typography>

      {isMobile && (
        <Link
          component="button"
          type="button"
          onClick={() => setExpanded((e) => !e)}
          sx={styles.toggle}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Link>
      )}

      <LegalDisclaimerPanel open={panelOpen} onClose={() => setPanelOpen(false)} />
    </Box>
  );
}
